import mapboxgl from "mapbox-gl";
import React from "react";
import { MapObject } from "../../../models/MapObject";
import PdfMapView, { PDF_MAP_MAX_ZOOM, PdfMapCamera, pdfMapBounds, PdfMapLogo } from "./PdfMapView";

export interface PdfMapCapturePadding {
    top: number;
    bottom: number;
    left: number;
    right: number;
}

interface Props {
    mapObjects: MapObject[];
    // What the automatic frame fits; defaults to everything drawn.
    fitTo?: MapObject[];
    project: any;
    mapStyle: string;
    width: number;
    height: number;
    padding: PdfMapCapturePadding;
    // Where to look, as framed in PdfMapFrameEditor. Without one the map zooms to fit the objects.
    camera?: PdfMapCamera | null;
    showGeotiff?: boolean;
    // Drawn on top of the items, and kept in the automatic frame.
    logo?: PdfMapLogo | null;
    onCapture: (imageDataUrl: string) => void;
}

// An off-screen map that draws the given map objects (see PdfMapView), frames them, and hands back a JPEG snapshot
// once everything has rendered. DOM overlays like waypoint step markers can't be in a canvas snapshot.
const PdfMapCapture: React.FC<Props> = ({
    mapObjects,
    fitTo,
    project,
    mapStyle,
    width,
    height,
    padding,
    camera,
    showGeotiff,
    logo,
    onCapture,
}) => {
    const captured = React.useRef(false);
    // Kept in refs so a new callback or object identity from the parent doesn't re-arm the snapshot. The parent
    // remounts this (with a new key) when what's drawn should change.
    const latest = React.useRef({ onCapture, fitObjects: fitTo ?? mapObjects, logo, padding, camera });
    React.useEffect(() => {
        latest.current = { onCapture, fitObjects: fitTo ?? mapObjects, logo, padding, camera };
    });
    const [framedMap, setFramedMap] = React.useState<mapboxgl.Map | null>(null);

    const frame = (map: mapboxgl.Map, logoDrawn: boolean) => {
        const { camera, fitObjects, logo, padding } = latest.current;
        if (camera) {
            map.jumpTo({ center: camera.center, zoom: camera.zoom, bearing: camera.bearing });
        } else {
            // A logo that couldn't be drawn isn't framed around.
            const bounds = pdfMapBounds(fitObjects, logoDrawn ? logo : null);
            if (!bounds.isEmpty()) map.fitBounds(bounds, { padding, maxZoom: PDF_MAP_MAX_ZOOM, animate: false });
        }
        setFramedMap(map);
    };

    React.useEffect(() => {
        const map = framedMap;
        if (!map) return;

        // Registered after the re-render that drew the items and sized them for the framed zoom, so the snapshot
        // includes them.
        const snapshot = () => {
            if (captured.current) return;
            captured.current = true;
            latest.current.onCapture(map.getCanvas().toDataURL("image/jpeg", 0.9));
        };
        map.once("idle", snapshot);
        // The map may already have gone idle before this listener existed (cached tiles, or a frame that didn't move the
        // camera), and then no further idle would come. A repaint makes it fire again once everything has settled.
        map.triggerRepaint();
        return () => {
            map.off("idle", snapshot);
        };
    }, [framedMap]);

    return (
        <div aria-hidden style={{ position: "fixed", left: -10000, top: 0, width, height }}>
            <PdfMapView
                mapObjects={mapObjects}
                project={project}
                mapStyle={mapStyle}
                width={width}
                height={height}
                interactive={false}
                showGeotiff={showGeotiff}
                logo={logo}
                onReady={frame}
            />
        </div>
    );
};

export default PdfMapCapture;
